/**
 * RescheduleFlow - Modal flow for rescheduling bookings
 * Feature 009: Workshop Booking & Job-Tracking System
 * T089
 */

import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
} from '../ui';
import { api } from '../../lib/api';

interface RescheduleFlowProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onOpenChange: (open: boolean) => void;
  /** Booking ID to reschedule */
  bookingId: string;
  /** Current scheduled date for display */
  currentScheduledAt?: Date | string | number;
  /** Booking reference for display */
  bookingReference?: string;
  /** Customer name for display */
  customerName?: string;
  /** Callback on successful reschedule */
  onSuccess?: (newBookingId: string) => void;
  /** Query keys to invalidate on success */
  invalidateKeys?: string[][];
}

interface TimeSlot {
  time: number;
  hour: string;
  available: boolean;
  bookingCount: number;
}

interface DayAvailability {
  date: string;
  slots: TimeSlot[];
  totalAvailable: number;
}

interface RescheduleResponse {
  oldBooking: { id: string; status: string };
  newBooking: {
    id: string;
    bookingReference?: string;
    scheduledAt: number;
    trackingToken?: string;
  };
  jobResetToQuotePending?: boolean;
  message?: string;
}

type Step = 'select' | 'confirm' | 'success';

export function RescheduleFlow({
  open,
  onOpenChange,
  bookingId,
  currentScheduledAt,
  bookingReference,
  customerName,
  onSuccess,
  invalidateKeys = [],
}: RescheduleFlowProps) {
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('select');
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [newBookingId, setNewBookingId] = useState<string | null>(null);

  // Fetch availability for selected date
  const { data: availability, isLoading: loadingAvailability } = useQuery({
    queryKey: ['availability', selectedDate],
    queryFn: async () => {
      const response = await api.get<DayAvailability>(
        `/api/bookings/availability?date=${selectedDate}`
      );
      return response;
    },
    enabled: open && step === 'select',
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setStep('select');
      setSelectedTime(null);
      setReason('');
      setError(null);
      setNewBookingId(null);
    }
  }, [open]);

  const rescheduleMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTime) throw new Error('No se ha seleccionado hora');

      const scheduledDate = new Date(selectedTime * 1000);

      const response = await api.post<RescheduleResponse>(`/api/bookings/${bookingId}/reschedule`, {
        newScheduledAt: scheduledDate.toISOString(),
        reason: reason.trim() || undefined,
      });

      return response;
    },
    onSuccess: (response) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
      queryClient.invalidateQueries({ queryKey: ['booking', bookingId] });
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });

      const newId = response.newBooking?.id;
      if (newId) {
        setNewBookingId(newId);
        setStep('success');
        onSuccess?.(newId);
      }
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const handleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
    setSelectedTime(null); // Reset time when date changes
  }, []);

  const handleSlotSelect = useCallback((slot: TimeSlot) => {
    if (slot.available) {
      setSelectedTime(slot.time);
    }
  }, []);

  const handleContinue = useCallback(() => {
    if (!selectedTime) {
      setError('Por favor, selecciona una fecha y hora');
      return;
    }
    setError(null);
    setStep('confirm');
  }, [selectedTime]);

  const handleConfirm = useCallback(() => {
    setError(null);
    rescheduleMutation.mutate();
  }, [rescheduleMutation]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const formatDate = (date: Date | string | number) => {
    const d = new Date(typeof date === 'number' && date < 10000000000 ? date * 1000 : date);
    return d.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatSelectedDateTime = () => {
    if (!selectedTime) return '';
    return formatDate(selectedTime);
  };

  // Calculate min date (tomorrow)
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            Reprogramar Cita
          </DialogTitle>
          <DialogDescription>
            {step === 'select' && 'Selecciona una nueva fecha y hora para la cita.'}
            {step === 'confirm' && 'Confirma los detalles de la reprogramación.'}
            {step === 'success' && 'La cita ha sido reprogramada exitosamente.'}
          </DialogDescription>
        </DialogHeader>

        {/* Step: Select Date/Time */}
        {step === 'select' && (
          <div className="space-y-4 py-4">
            {/* Current booking info */}
            {currentScheduledAt && (
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-500">Fecha actual:</p>
                <p className="font-medium text-gray-900 capitalize">
                  {formatDate(currentScheduledAt)}
                </p>
              </div>
            )}

            {/* Date Picker */}
            <div className="space-y-2">
              <Label htmlFor="reschedule-date">Nueva fecha</Label>
              <input
                id="reschedule-date"
                type="date"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={selectedDate}
                onChange={handleDateChange}
                min={minDate.toISOString().split('T')[0]}
              />
            </div>

            {/* Time Slots */}
            <div className="space-y-2">
              <Label>Horario disponible</Label>

              {loadingAvailability ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : !availability?.slots.length ? (
                <p className="text-sm text-gray-500 py-4 text-center">
                  No hay horarios disponibles para esta fecha.
                </p>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {availability.slots.map((slot) => (
                    <button
                      key={slot.time}
                      type="button"
                      disabled={!slot.available}
                      onClick={() => handleSlotSelect(slot)}
                      className={`
                        px-3 py-2 text-sm font-medium rounded-md transition-colors
                        ${
                          selectedTime === slot.time
                            ? 'bg-blue-600 text-white'
                            : slot.available
                            ? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                            : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        }
                      `}
                    >
                      {slot.hour}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Reason (optional) */}
            <div className="space-y-2">
              <Label htmlFor="reschedule-reason">Motivo (opcional)</Label>
              <textarea
                id="reschedule-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Motivo de la reprogramación..."
                className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                maxLength={500}
              />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Step: Confirm */}
        {step === 'confirm' && (
          <div className="space-y-4 py-4">
            <div className="rounded-lg border p-4 space-y-4">
              {/* Old date -> New date */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Fecha anterior</p>
                  <p className="text-sm text-gray-600 capitalize line-through">
                    {currentScheduledAt ? formatDate(currentScheduledAt) : 'N/A'}
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Nueva fecha</p>
                  <p className="text-sm font-medium text-blue-600 capitalize">
                    {formatSelectedDateTime()}
                  </p>
                </div>
              </div>

              {/* Booking details */}
              <div className="pt-3 border-t space-y-2">
                {bookingReference && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Referencia:</span>
                    <span className="font-medium">{bookingReference}</span>
                  </div>
                )}
                {customerName && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Cliente:</span>
                    <span className="font-medium">{customerName}</span>
                  </div>
                )}
                {reason && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Motivo:</span>
                    <span className="font-medium">{reason}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-700">
              <strong>Nota:</strong> Se creará una nueva cita y la anterior será cancelada automáticamente.
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="py-8 text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Cita Reprogramada
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                La cita ha sido reprogramada para:
              </p>
              <p className="text-lg font-medium text-blue-600 mt-2 capitalize">
                {formatSelectedDateTime()}
              </p>
            </div>
            {newBookingId && (
              <p className="text-xs text-gray-500">
                Nueva referencia: {newBookingId.slice(0, 8).toUpperCase()}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step === 'select' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleContinue}
                disabled={!selectedTime}
              >
                Continuar
              </Button>
            </>
          )}

          {step === 'confirm' && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep('select')}
                disabled={rescheduleMutation.isPending}
              >
                Volver
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={rescheduleMutation.isPending}
              >
                {rescheduleMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Reprogramando...
                  </>
                ) : (
                  'Confirmar Reprogramación'
                )}
              </Button>
            </>
          )}

          {step === 'success' && (
            <Button onClick={handleClose} className="w-full sm:w-auto">
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
